import type { ReactNode } from 'react';
import { Archive } from 'lucide-react';
import { Card } from './Card';
import { StatusBadge } from './StatusBadge';
import type { BackupStatus } from '../hooks/useStatus';
import { ago, backupHealth, formatBytes, setIdToDate } from '../utils/backups';

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex justify-between gap-4">
    <span className="text-dark-400">{label}</span>
    <span className="text-white text-right">{children}</span>
  </div>
);

export function BackupsCard({ backups }: { backups: BackupStatus }) {
  const health = backupHealth(backups);
  const store = backups.offsiteStore;
  const o = backups.onServer;
  const r = backups.restoreTest;
  const a = backups.awsCopy;
  const ra = backups.restoreTestAws;
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Archive className="h-5 w-5 text-dark-400" />
          {store !== undefined ? 'Off-Server Backup Store' : 'Portfolio Backups'}
        </h2>
        <StatusBadge status={health} />
      </div>

      {store !== undefined ? (
        <div className="text-sm space-y-1">
          {store ? (
            <>
              <Row label="Newest stored set">{store.newestSetId ?? 'none'} ({ago(setIdToDate(store.newestSetId))})</Row>
              <Row label="Last arrival">{store.lastResult === 'ok' ? 'verified and stored' : store.lastResult === 'failed' ? 'FAILED verification (quarantined)' : 'unknown'}, {ago(store.lastReceivedAt)}</Row>
              <Row label="Sets stored">{store.setsStored ?? 'unknown'} · {formatBytes(store.bytes)} (oldest {store.oldestSetId ?? 'none'})</Row>
              <Row label="Quarantined">{store.quarantined ?? 'unknown'}</Row>
              <Row label="Checked">{ago(store.checkedAt)}</Row>
            </>
          ) : (
            <div className="text-dark-400">Backup store status unavailable.</div>
          )}
          <div className="text-xs text-dark-500 pt-2">
            Encrypted copies from NYC3, verified against each set's signed manifest on arrival. This host holds no decryption key. Retention: 15 daily sets plus the first set of each of the last 12 months.
          </div>
        </div>
      ) : (
        <div className="text-sm space-y-1">
          <Row label="Last set">{o?.lastSetId ?? 'none'} · {o?.status ?? 'unknown'} ({ago(o?.lastSetFinishedAt)})</Row>
          <Row label="Archives">{o?.archivesOk ?? '?'} ok{o && (o.archivesFailed ?? 0) > 0 ? `, ${o.archivesFailed} FAILED` : ''}</Row>
          <Row label="On this server">{o?.setsRetained ?? '?'} sets · {formatBytes(o?.bytes)}</Row>
          {(backups.offsite ?? []).map((p) => (
            <Row key={p.target} label={`Copy to ${p.target.toUpperCase()}`}>{p.status ?? 'unknown'} ({ago(p.pushedAt)})</Row>
          ))}
          {a !== undefined && (
            <Row label="Copy to AWS (locked)">
              {a ? `${a.status ?? 'unknown'} · ${a.setsUploaded ?? '?'} sets · last ${a.lastSetId ?? 'none'} (${ago(a.checkedAt)})` : 'status unavailable'}
            </Row>
          )}
          <Row label="Restore test">
            {r ? `${r.status ?? 'unknown'} from ${r.source?.toUpperCase() ?? '?'} · ${r.checksPassed ?? '?'} passed, ${r.checksFailed ?? '?'} failed (${ago(r.finishedAt)})` : 'not run yet'}
          </Row>
          {a !== undefined && (
            <Row label="Restore test (AWS)">
              {ra ? `${ra.status ?? 'unknown'} · ${ra.checksPassed ?? '?'} passed, ${ra.checksFailed ?? '?'} failed (${ago(ra.finishedAt)})` : 'not run yet'}
            </Row>
          )}
          {backups.ventures && backups.ventures.length > 0 && (
            <div className="pt-3">
              <div className="text-dark-400 mb-1">By venture (latest set)</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
                {backups.ventures.map((v) => (
                  <div key={v.venture} className="flex justify-between gap-2 text-xs">
                    <span className="text-dark-300">{v.venture}</span>
                    <span className="text-dark-400">{v.archives ?? '?'} · {formatBytes(v.bytes)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-dark-500 pt-2">
            Daily at 04:30 UTC: every database, Redis store and data folder, encrypted per venture before it leaves this server, kept here 15 days and copied to SFO2 and, at 05:15, to AWS London under a compliance lock (35 days daily, 12 months monthly; no one can delete it early). Restore tests: from SFO2 every Sunday, from AWS on the first Sunday of each month. Key {backups.keyFingerprint ? `…${backups.keyFingerprint.slice(-16)}` : 'unknown'}. No controls here — restores are run over SSH (see the runbook in backup.3pplea.com).
          </div>
        </div>
      )}
    </Card>
  );
}
